import React, { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX_MB = 8; // pre-compression source
const MAX_BYTES = MAX_MB * 1024 * 1024;

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// Compress via canvas, target ~1MB payload. Keeps EXIF-lite JPEG.
const compressImage = (dataUrl, { maxDim = 1600, quality = 0.82, targetBytes = 1_100_000 } = {}) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const ratio = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      let q = quality;
      let out = canvas.toDataURL("image/jpeg", q);
      // Base64 size is ~1.37x binary size; loop-tighten until close to target
      while (out.length > targetBytes * 1.37 && q > 0.4) {
        q -= 0.1;
        out = canvas.toDataURL("image/jpeg", q);
      }
      resolve(out);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

/**
 * Drag-and-drop multi-photo uploader with client-side compression.
 * value: current array of data URLs (parent-controlled).
 * captions/onCaptionsChange (optional): parallel string[] captions per index.
 * showCaptions: render caption inputs in preview grid.
 */
export default function PhotoUploader({
  value = [],
  onChange,
  captions = [],
  onCaptionsChange,
  showCaptions = false,
  maxPhotos = 10,
  testidPrefix = "photo",
  label = "Unggah foto",
}) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const remaining = Math.max(0, maxPhotos - value.length);
    if (remaining <= 0) {
      toast.error(`Maksimum ${maxPhotos} foto`);
      return;
    }
    const accepted = list.slice(0, remaining).filter((f) => {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: hanya file gambar diperbolehkan`); return false; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name}: melebihi ${MAX_MB} MB`); return false; }
      return true;
    });
    if (!accepted.length) return;
    setBusy(true);
    try {
      const raws = await Promise.all(accepted.map(readAsDataURL));
      const compressed = await Promise.all(raws.map((d) => compressImage(d)));
      onChange([...(value || []), ...compressed]);
      if (showCaptions && onCaptionsChange) {
        onCaptionsChange([...(captions || []), ...compressed.map(() => "")]);
      }
      toast.success(`${compressed.length} foto siap · dikompresi otomatis`);
    } finally {
      setBusy(false);
    }
  }, [maxPhotos, value, onChange, captions, onCaptionsChange, showCaptions]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    addFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => {
    onChange(value.filter((_, j) => j !== i));
    if (showCaptions && onCaptionsChange) {
      onCaptionsChange((captions || []).filter((_, j) => j !== i));
    }
  };

  const setCaptionAt = (i, txt) => {
    if (!onCaptionsChange) return;
    const arr = [...(captions || [])];
    while (arr.length <= i) arr.push("");
    arr[i] = txt;
    onCaptionsChange(arr);
  };

  return (
    <div data-testid={`${testidPrefix}-uploader`}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid={`${testidPrefix}-dropzone`}
        className={
          "cursor-pointer border-2 border-dashed rounded-2xl p-6 text-center transition-colors duration-200 " +
          (drag ? "border-[#1E3A8A] bg-[#EFF6FF]" : "border-[#EAE7E0] bg-[#FAFAF8] hover:border-[#1E3A8A] hover:bg-[#EFF6FF]/50")
        }
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          data-testid={`${testidPrefix}-file-input`}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
        <div className="mx-auto w-10 h-10 rounded-full bg-white border border-[#EAE7E0] flex items-center justify-center mb-2">
          <Upload size={16} className="text-[#1E3A8A]" />
        </div>
        <div className="text-sm text-[#1F2937] font-medium">{busy ? "Mengompresi foto…" : label}</div>
        <div className="text-xs text-[#4B5563] mt-1">Seret & lepas atau klik. Maks {maxPhotos} foto. Foto akan dikompresi ke ~1 MB otomatis.</div>
      </div>

      {value.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid={`${testidPrefix}-preview-grid`}>
          {value.map((src, i) => (
            <div key={i} className="relative rounded-xl overflow-hidden border border-[#EAE7E0] bg-[#F3F1ED]" data-testid={`${testidPrefix}-preview-${i}`}>
              <div className="relative group aspect-[4/3]">
                {src ? (
                  <img src={src} alt={`upload-${i}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ImageIcon size={18} className="text-[#4B5563]" /></div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                  data-testid={`${testidPrefix}-remove-${i}`}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Hapus"
                >
                  <X size={14} />
                </button>
              </div>
              {showCaptions && (
                <input
                  type="text"
                  maxLength={140}
                  placeholder="Caption (opsional)…"
                  value={captions[i] || ""}
                  onChange={(e) => setCaptionAt(i, e.target.value)}
                  data-testid={`${testidPrefix}-caption-${i}`}
                  className="w-full text-xs px-2 py-1.5 border-t border-[#EAE7E0] bg-white focus:outline-none focus:bg-[#EFF6FF]"
                />
              )}
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-[#4B5563] mt-2">{value.length}/{maxPhotos} foto</div>
    </div>
  );
}
